import moment from 'moment';
import sumBy from 'lodash.sumby';
import {
    AvgDataItem,
    BaseDataItem,
    PeakDataItem,
    ExternalTotalsDataItem,
    ComparisonBaseDataItem,
    AccoladeDataItem,
    Timescales
} from '../../../service/valkyrie';

export const getSum = (
    data: PeakDataItem[] | AvgDataItem[],
    islands: string[],
    key: keyof PeakDataItem | keyof AvgDataItem
): number =>
    sumBy(
        data.filter(({creative_island_code}) => islands.includes(creative_island_code)),
        key
    ) || 0;

export const getTrend = (previousValue: number, value: number, fractionDigits = 1): number => {
    const fractions = 10 ** fractionDigits;
    const difference = previousValue ? value - previousValue : 0;

    return (
        Math.round((previousValue ? (difference / previousValue) * 100 : 0) * fractions) / fractions
    );
};

export const getDiffTrend = (data: MappedDataItem[], key: string, previousKey: string): number =>
    sumBy(data, dataItem => dataItem[key] - (dataItem[previousKey] || dataItem[key])) || 0;

export const getPercentTrend = (previousValue: number, value: number): number => {
    if (!previousValue || !value) {
        return 0;
    }

    return Math.round(((value - previousValue) / previousValue) * 100);
};

export interface FormatDataParams {
    data: BaseDataItem[];
    timeKey: string;
    valueKey: string;
    islands: string | string[];
}

export interface FormattedDataItem {
    [key: string]: string;
    time: string;
}

export interface MappedDataItem {
    [key: string]: string;
    creative_island_code: string;
}

export interface FormatData {
    list: FormattedDataItem[];
    empty: string[];
}

export const formatData = ({
    data,
    timeKey,
    valueKey,
    islands: islandsParams
}: FormatDataParams): FormatData => {
    if (!data || !data.length) {
        return {list: [], empty: []};
    }

    const islands = Array.isArray(islandsParams) ? islandsParams : [islandsParams];

    const initialValue: {[key: string]: number} = {};
    const empty: {[key: string]: boolean} = {};
    islands.forEach(code => {
        initialValue[code] = 0;
        empty[code] = true;
    });

    const result = Object.values(
        data.reduce<{[key: string]: FormattedDataItem}>((acc, item) => {
            if (!islands.includes(item.creative_island_code)) {
                return acc;
            }

            const time = new Date(item[timeKey]).toISOString();
            const value = item[valueKey];
            const {creative_island_code} = item;

            if (empty[creative_island_code] && value) {
                empty[creative_island_code] = false;
            }

            if (acc[time]) {
                acc[time][creative_island_code] = value;
            } else {
                acc[time] = {
                    ...initialValue, // Fill gaps with 0
                    [creative_island_code]: value,
                    time
                };
            }

            return acc;
        }, {})
    );

    return {
        list: result,
        empty: Object.entries(empty)
            .filter(([_, isEmpty]) => isEmpty)
            .map(([code]) => code)
    };
};

export const getTotal = (data: {[key: string]: string}[], key: string): number =>
    sumBy(data, item => item[key]) || 0;

export const getLastFullPeriodData = (
    data: ComparisonBaseDataItem[] = [],
    timescale: Timescales.weekly | Timescales.monthly
): ComparisonBaseDataItem[] => {
    const today = new Date();

    const startOfLastFullWeek = moment(today)
        .subtract(1, 'weeks')
        .startOf('week')
        .format('YYYY-MM-DD');
    const startOfLastFullMonth = moment(today)
        .subtract(1, 'months')
        .startOf('month')
        .format('YYYY-MM-DD');

    const TimescalesMap = {
        [Timescales.weekly]: {period: 'week', startDate: startOfLastFullWeek},
        [Timescales.monthly]: {period: 'month', startDate: startOfLastFullMonth}
    };

    return data.filter(
        dataItem => dataItem[TimescalesMap[timescale].period] === TimescalesMap[timescale].startDate
    );
};

export const getComparisonSum = (
    data: ComparisonBaseDataItem[],
    key: keyof ComparisonBaseDataItem
): number => sumBy(data, key) || 0;

export const getPrevDayParams = () => {
    const today = new Date();

    return {
        fromTs: moment(today)
            .subtract(48, 'hours')
            .valueOf(),
        toTs: moment(today)
            .subtract(24, 'hours')
            .valueOf()
    };
};

export const getPrevTwoDaysParams = () => {
    const today = new Date();

    return {
        fromTs: moment(today)
            .subtract(72, 'hours')
            .valueOf(),
        toTs: moment(today)
            .subtract(24, 'hours')
            .valueOf()
    };
};

export const getLastFullWeekDates = () => {
    const today = new Date();

    return {
        fromTs: moment(today)
            .subtract(1, 'weeks')
            .startOf('week')
            .valueOf(),
        toTs: moment(today)
            .subtract(1, 'weeks')
            .endOf('week')
            .valueOf()
    };
};

export const getLastFull7DaysDates = () => {
    const today = new Date();

    return {
        fromTs: moment(today)
            .subtract(7, 'days')
            .startOf('day')
            .valueOf(),
        toTs: moment(today)
            .subtract(1, 'days')
            .endOf('day')
            .valueOf()
    };
};

export const getPreviousFull7DaysDates = () => {
    const today = new Date();

    return {
        fromTs: moment(today)
            .subtract(14, 'days')
            .startOf('day')
            .valueOf(),
        toTs: moment(today)
            .subtract(8, 'days')
            .endOf('day')
            .valueOf()
    };
};

export const getMetric = (timestamp, metric, {fromTs, toTs}): number => {
    if (moment(timestamp).isBetween(fromTs, toTs, 'day', '[]')) {
        return metric;
    }
};

export const getActiveUsers = (activeUsers, creativeIslandCode, date: number) => {
    const activeUser = activeUsers.filter(
        ({day, creative_island_code}) =>
            moment(date).isSame(day, 'day') && creative_island_code === creativeIslandCode
    )[0];

    return activeUser ? activeUser.unique_users : 0;
};

export const getTotalPlay = (plays: ExternalTotalsDataItem[]): number =>
    plays[0] && plays[0].payload ? plays[0].payload.total_plays : 0;

export const getTotalPlays = (plays, creativeIslandCode, date: number) => {
    const play = plays.filter(
        ({timestamp, creative_island_code}) =>
            moment(date).isSame(timestamp, 'day') && creative_island_code === creativeIslandCode
    )[0];

    return play ? play.total_plays : 0;
};

export const getActivations = (activations: AccoladeDataItem[]) =>
    activations.reduce((reducedStats, {payload: {accolades: stats}}) => {
        Object.entries(stats).forEach(([id, activations]) => {
            const accoladeActivations = reducedStats[id] || 0;

            reducedStats[id] = accoladeActivations + Number(activations);
        });

        return reducedStats;
    }, {});
